import { API_BASE } from "../config";
import { getValidIdToken, refrescarToken, marcarSesionExpirada } from "./auth";

/**
 * fetch hacia la API con un token VÁLIDO adjunto.
 *
 * Adjunta `Authorization: Bearer` y el backend deriva el alcance del token: es el
 * punto donde "el alcance sale del token, nunca del request" se vuelve real desde
 * el cliente.
 *
 * Y renueva el token cuando toca. Antes mandaba lo que hubiera en sessionStorage;
 * como los id tokens de Cognito duran una hora, a partir de los 60 minutos TODA la
 * app devolvía 401 y cada pantalla lo traducía a su manera ("no hay residenciales
 * en tu alcance"). El usuario no tenía forma de saber que solo debía volver a entrar.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getValidIdToken();
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;

  const res = await fetch(url, { ...init, headers: conToken(init.headers, token) });
  /*
   * Sin token no hay nada que reintentar. Si es porque la renovación falló,
   * `getValidIdToken` ya marcó la sesión como caída (auth.ts): ese es el único
   * sitio que lo decide, para que ningún camino se quede callado.
   */
  if (res.status !== 401 || !token) return res;

  /*
   * El servidor rechazó un token que dábamos por bueno. Puede ser el reloj del
   * equipo desfasado, o que Cognito lo invalidara. Se reintenta UNA vez con uno
   * pedido a Cognito a la fuerza (no el cacheado, que es el rechazado); si vuelve
   * a fallar, se AVISA (no se expulsa: ver auth.ts).
   */
  diagnostico("401 con token vigente", url, token, res);
  const fresco = await refrescarToken();
  if (!fresco) {
    // `refrescarToken` ya marcó la sesión como caída.
    console.warn("[ikids] no se pudo renovar el token: Cognito no devolvió sesión.");
    return res;
  }
  const reintento = await fetch(url, { ...init, headers: conToken(init.headers, fresco) });
  if (reintento.status === 401) {
    diagnostico("401 incluso con token recién renovado", url, fresco, reintento);
    marcarSesionExpirada();
  }
  return reintento;
}

/**
 * Deja en la consola lo que hace falta para saber POR QUÉ el servidor rechazó el
 * token, sin filtrar el token entero. Existe porque el backend responde "Token
 * inválido o expirado" a propósito —no le dice a quien prueba tokens cuál de las
 * dos cosas falló— y desde fuera esas dos causas se arreglan distinto: una es
 * volver a entrar, la otra es un problema de configuración.
 */
function diagnostico(que: string, url: string, token: string, res: Response): void {
  let exp = "ilegible";
  let aud = "?";
  let iss = "?";
  try {
    const p = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    exp = p.exp ? `${new Date(p.exp * 1000).toISOString()} (${p.exp - Math.floor(Date.now() / 1000)}s)` : "sin exp";
    aud = String(p.aud ?? "sin aud");
    iss = String(p.iss ?? "sin iss");
  } catch {
    /* token ilegible: eso ya es el diagnóstico */
  }
  console.warn(
    `[ikids] ${que}\n  url: ${url}\n  http: ${res.status}\n  token.exp: ${exp}\n  token.aud: ${aud}\n  token.iss: ${iss}`
  );
}

function conToken(base: HeadersInit | undefined, token: string | null): Headers {
  const headers = new Headers(base);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return headers;
}
